import type { Phase } from "./intake-flow-types"

// The ONE progress system for the consumer flow: a single bar + one label.
// Photos are half the bar and move with every shot, because that's the part
// people actually wait through. Pure; unit-tested.
//
//   contact 0–10% · photos 10–60% · questions 60–85% · result 85–100%

export interface ProgressInput {
  phase: Phase
  photos: { done: number; total: number }
  questions: { index: number; count: number }
}

export interface FlowProgress {
  // 0–1
  value: number
  label: string
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n))

export function progressFor({ phase, photos, questions }: ProgressInput): FlowProgress {
  switch (phase) {
    case "contact":
      return { value: 0.04, label: "Your details" }
    case "handoff":
      return { value: 0.1, label: "Photos · on your phone" }
    case "photos": {
      if (photos.total <= 0) return { value: 0.1, label: "Photos" }
      const done = Math.min(photos.done, photos.total)
      return {
        value: 0.1 + 0.5 * clamp01(done / photos.total),
        label: `Photos · ${done} of ${photos.total}`,
      }
    }
    case "extras": {
      const count = Math.max(1, questions.count)
      const index = Math.min(Math.max(0, questions.index), count - 1)
      return {
        value: 0.6 + 0.25 * (index / count),
        label: `A few questions · ${index + 1} of ${count}`,
      }
    }
    case "reveal":
      return { value: 0.85, label: "What we see" }
    case "offer":
      return { value: 0.9, label: "Sent to the shop" }
    case "booking":
      return { value: 0.94, label: "Pick a time" }
    case "done":
      return { value: 1, label: "All set" }
  }
}
